import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

import {
    GlobalEventBusService,
    hd,
    XDeviceService,
} from '@nx-fe/services';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { GlobalEvents } from 'app/core/global-event-constants';
import { VideoRecordingStatus } from './video-recording.service';
import { IWorkPanelWindow } from './work-panel.service';
import { FileManagerService } from './file-manager.service';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { AnnotationState, IAgentVideoRecordingSettings, IRCCanvasConfig, IScreenCaptureConfig, IDeviceStateRegistryTransferable, IDeviceStateTransferable, IVideoRecordingUserConfig } from './remote-control.interfaces';
import { XsI18nPipe } from '@nx-fe/pipes';

type IDeviceModel = hd.IDeviceModel;

export enum RecordingState {
    NotRunning,
    Running,
    Paused,
    PendingAccept,
    UploadingVideo,
    TranscodingVideo,
}

export class RemoteControlDeviceState implements IDeviceStateTransferable {
    /** Hide rc progress bar */
    public loading = true;

    // active means a rc process is started
    public active = false;

    // connected means connection is established, must happen with active = true first.
    public connected = false;

    // running means that the connected session was accepted on the device (if needed)
    // and the first full screen has been rendeered in both RC protocols (streaming or not),
    // This is different from firstScreenLoaded
    public get running() {
        return this._running.value;
    }
    public set running(isRunning: boolean) {
        this._running.next(isRunning);
    }

    public minimized = false;
    public poppedOut = false;
    public poppingOut = false;

    public recording = false;
    public recording$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    // Recording by agent on the device including microphone audio
    public recordingWithAudio = false;
    public recordingState: RecordingState = RecordingState.NotRunning;
    public recordingPaused = false;
    public recordingPopoverOpen = false;

    public device: IDeviceModel;
    public mcDeviceId: string;

    public errorMessage = '';
    public errorMessageCaption = '';
    public errorMessageDescription = '';
    public hasError = false;

    public annotations: AnnotationState;

    public startTime: Date;

    public popoutWindow: IWorkPanelWindow;

    public firstScreenLoaded = false; // TODO NT: Check if we can remove this and instead use the deviceContext.capture.fullscreenLoaded

    public videoRecordingPrefs: IVideoRecordingUserConfig;

    public screenCapturePrefs: IScreenCaptureConfig;

    // Settings for agent-side recording with audio
    public agentVideoRecordingPrefs: IAgentVideoRecordingSettings;

    public rcCanvasConfigPrefs: IRCCanvasConfig;

    // public deviceLogConfigPrefs: IDeviceLogConfig;
    private _running = new BehaviorSubject<boolean>(false);
    public running$ = this._running.asObservable();
}

class DeviceIdToDeviceStateMap {
    [deviceId: string]: RemoteControlDeviceState;
}

class DeviceIdCallbackMap {
    [deviceId: string]: Function[];
}

export class DevicePermissionsMap {
    [deviceId: string]: hd.IRemoteControlPermissionsModel;
}

@Injectable()
export class DeviceStateRegistryService implements OnDestroy, IDeviceStateRegistryTransferable {
    /** The shared device state */
    private states: DeviceIdToDeviceStateMap = {};
    private onDestroy$ = new Subject<void>();
    private devicePermissions: DevicePermissionsMap = {};
    private stopRecordingCallback: Function;
    private startRecordingCallback: Function;

    private remoteControlPopoutCallbacks: Array<(poppedOut: boolean) => void> = [];
    private remoteControlStartedCallback: DeviceIdCallbackMap = {};
    private remoteControlEndedCallback: DeviceIdCallbackMap = {};
    private remoteControlErrorCallback: DeviceIdCallbackMap = {};
    private remoteControlLoginCallback: DeviceIdCallbackMap = {};

    public readonly videoRecordingStatusChanged: BroadcastService<VideoRecordingStatus> = new BroadcastService(
        BroadcastChannels.onVideoRecordingStatusChanged
    );
    public readonly deviceSnapshotLockoutStatusChanged: BroadcastService<hd.DeviceSnapshotRequestStatus> =
        new BroadcastService(BroadcastChannels.onDeviceSnapshotRequestInProgress);
    public readonly deviceStateSynchronizeChanged: BroadcastService<RemoteControlDeviceState> =
        new BroadcastService<RemoteControlDeviceState>(BroadcastChannels.onDeviceStateSynchronizeChanged);
    public readonly popoutRemoteControlStop: BroadcastService<IDeviceModel> = new BroadcastService<IDeviceModel>(
        BroadcastChannels.onPopoutRemoteControlStop
    );
    public readonly remoteControlErrorBroadcast: BroadcastService<void> = new BroadcastService<void>(
        BroadcastChannels.onRemoteControlError
    );
    public readonly remoteControlEndedMessageForPopOut: BroadcastService<string> = new BroadcastService(
        BroadcastChannels.onRemoteControlEndedMessageForPopOut
    );
    // When we start RC from popOut 'rcSessionEstablished' from mcrc-com-Session only comes to popOut remoteControlService.
    // To start remoteControlActivity correctly we should send a message to the remoteControlService main window.
    public readonly remoteControlSessionEstablished: BroadcastService<string> = new BroadcastService<string>(
        BroadcastChannels.onRemoteControlSessionEstablished
    );

    private videoRecordingState: RecordingState = RecordingState.NotRunning;

    constructor(
        private fileManagerService: FileManagerService,
        private globalEventBusService: GlobalEventBusService,
        private xDeviceService: XDeviceService,
        private saI18nPipe: XsI18nPipe,
    ) {
        this.videoRecordingStatusChanged.onMessage = this.handleVideoRecordingStatusChange;
        this.deviceStateSynchronizeChanged.onMessage = this.handleDeviceStateSynchronizeChanged;
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();

        for (const key in this.states) {
            if (this.states.hasOwnProperty(key)) {
                const state = this.states[key];
                // this.closeSession(state.device);
                this.resetVideoRecordingState(state[key]);
                state.recording$.complete();
            }
        }

        if (this.deviceSnapshotLockoutStatusChanged) {
            this.deviceSnapshotLockoutStatusChanged.close();
        }

        if (this.popoutRemoteControlStop) {
            this.popoutRemoteControlStop.close();
        }

        if (this.remoteControlSessionEstablished) {
            this.remoteControlSessionEstablished.close();
        }

        if (this.remoteControlEndedMessageForPopOut) {
            this.remoteControlEndedMessageForPopOut.close();
        }

    }

    private handleVideoRecordingStatusChange = (statusChange: VideoRecordingStatus): void => {
        this.videoRecordingState = statusChange.state;
    };

    public resetVideoRecordingState = (state?: RemoteControlDeviceState) => {
        this.videoRecordingState = RecordingState.NotRunning;
        if (state) {
            state.recordingState = RecordingState.NotRunning;
            // resetting video recording state
            this.videoRecordingStatusChanged.postMessage({
                state: RecordingState.NotRunning,
                withAudio: state.recordingWithAudio,
            });
        }
    };

    private handleDeviceStateSynchronizeChanged = (state: RemoteControlDeviceState): void => {
        if (this.states[state.device.deviceId] && state) {
            this.states[state.device.deviceId].active = state.active;
            this.states[state.device.deviceId].connected = state.connected;
            this.states[state.device.deviceId].poppedOut = state.poppedOut;
        }
    };

    /** returns the device state for ket *deviceId* or the first state, if the deviceId is not supplied*/
    public first = (searchInactive?: boolean): RemoteControlDeviceState => {
        if (searchInactive) {
            const firstKey = Object.keys(this.states)[0];
            return !firstKey ? undefined : this.states[firstKey];
        }
        for (const key in this.states) {
            const state = this.states[key];
            if (state.active) {
                return state;
            }
        }
        return undefined;
    }

    public isEmpty = (): boolean => { return Object.keys(this.states).length === 0; }

    public getState = (deviceId: string): RemoteControlDeviceState => this.states[deviceId];
    public resetState = (deviceId: string) => delete this.states[deviceId];

    public filterAndApply = (filter: (state: RemoteControlDeviceState) => boolean, callback: (state: RemoteControlDeviceState) => void): void => {
        for (const key in this.states) {
            if (filter(this.states[key])) {
                callback(this.states[key]);
            }
        }
    }

    public copyFrom = (source: DeviceStateRegistryService, deviceId: string, state: RemoteControlDeviceState) => {
        this.states[deviceId] = state;
        this.devicePermissions[state.device.deviceId] = source.devicePermissions[deviceId];
    }

    public transfer = (deviceStateRegistry: IDeviceStateRegistryTransferable) => {
        this.stopRecordingCallback = deviceStateRegistry.getStopRecordingCallback();
        this.startRecordingCallback = deviceStateRegistry.getStartRecordingCallback();
    }

    public getOrCreateState = (deviceId: string): RemoteControlDeviceState => {
        if (this.getState(deviceId) === undefined) {
            this.states[deviceId] = new RemoteControlDeviceState();
            this.states[deviceId].loading = false;
        }

        return this.getState(deviceId);
    };

    public getPermissions = (deviceId: string): hd.IRemoteControlPermissionsModel => {
        const permissions = this.devicePermissions[deviceId];

        if (!!permissions) {
            // console.log('cached permissions')
        } else {
            if (!this.isFetchingPermissions) {
                this.isFetchingPermissions = true;
                this.xDeviceService.getDevicePermissions(deviceId).subscribe(result => {
                    this.devicePermissions[deviceId] = result as any;
                    this.isFetchingPermissions = false;
                });
            }
        }
        return permissions;
    };

    public hasPermission = (state: RemoteControlDeviceState, permission: hd.RemoteControlPermissions): boolean => {
        if (state?.device) {
            const devicePermissions = this.getPermissions(state.device.deviceId);
            return devicePermissions?.permissions && (devicePermissions.permissions & permission) !== 0;
        }
        return false;
    }

    /** Checks if the service has received the MC permissions for the particular device, or it is still fetching */
    public knowsPermissions = (deviceId: string): boolean => {
        return !this.isFetchingPermissions && this.devicePermissions[deviceId] !== undefined;
    }

    public getCurrentState = (): RemoteControlDeviceState => {
        for (const key in this.states) {
            if (this.states[key].active && !this.states[key].loading && this.states[key].connected) {
                return this.states[key];
            }
        }
        return null;
    };

    public getConnectedOrErrorState = (): RemoteControlDeviceState => {
        for (const key in this.states) {
            if (this.states[key].hasError || this.states[key].connected) {
                return this.states[key];
            }
        }
        return null;
    };


    /**
   * Returns a value indicating whether the device supports remote control or not..
   *
   * Note that this does not take into account whether the current user has permissions
   * to remote control the device (see getDevicePermissions).
   *
   * @param device The device to query for remote control support.
   */
    public canSupportRC = (device: IDeviceModel): boolean => device && device.supportsRC;

    /**
     * Returns a value indicating whether the device can be remote controlled or not.
     *
     * Note that this does not take into account whether the current user has permissions
     * to remote control the device (see getDevicePermissions).
     *
     * @param device The device to query for remote control capability.
     */
    public canRemoteControl = (device: IDeviceModel): boolean => this.canSupportRC(device) && device.isAgentOnline;

    public remoteControlActiveCount = (): number =>
        Object.keys(this.states).filter(val => this.states[val].active).length;

    private isFetchingPermissions = false;

    public remoteControlActiveMap = (): DeviceIdToDeviceStateMap => this.states;

    public isRemoteControlActive = (device: IDeviceModel): boolean =>
        device !== undefined && this.states[device.deviceId] !== undefined && this.states[device.deviceId].active;
    public isRemoteControlErrorState = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].hasError;

    public isRemoteControlConnected = (deviceId: string): boolean =>
        this.states[deviceId] && this.states[deviceId].connected;

    /**
     * checking if the current RC session is connected and accepted (the RC screen/video has been received)
     * Handles both the old protocol (via the device context) and the new RC protocol (state based)
     */
    public isCurrentRemoteControlRunning = (): boolean => {
        const deviceContextHandler = RcContext.instance.deviceContextHandler;
        if (deviceContextHandler) {
            const ctx = deviceContextHandler.currentContext();
            return ctx.device.fullscreenLoaded;
        }
        return this.getCurrentState()?.running || false;
    };

    public isCurrentRemoteControlRunning$ = (): Observable<boolean> => {
        const deviceContextHandler = RcContext.instance.deviceContextHandler;
        if (deviceContextHandler) {
            const ctx = deviceContextHandler.currentContext();
            return ctx.device.fullScreenLoaded$;
        }
        return this.getCurrentState()?.running$ || of(false);
    };

    public isLoading = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].loading;

    public isMinimized = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].minimized;

    public isPoppedOut = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].poppedOut;

    public isRecording = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].recording;

    public isRunning = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].running;

    public isRecordingPendingAccept = (): boolean =>
        this.videoRecordingState && this.videoRecordingState === RecordingState.PendingAccept;

    public isRecordingPopoverOpen = (device: IDeviceModel): boolean =>
        device !== undefined &&
        this.states[device.deviceId] !== undefined &&
        this.states[device.deviceId].recordingPopoverOpen;

    public anyRcVisible = (): boolean => {
        let visible = false;
        for (const key in this.states) {
            if (this.states.hasOwnProperty(key)) {
                visible =
                    visible ||
                    (this.states[key].active &&
                        !this.states[key].minimized &&
                        !this.states[key].loading &&
                        this.states[key].connected);
            }
        }
        return visible;
    };

    public anyRcConnected = (): boolean => {
        let connected = false;
        for (const key in this.states) {
            if (this.states.hasOwnProperty(key)) {
                connected = connected || this.states[key].connected;
            }
        }
        return connected;
    };

    public minimizeToggle = (device: IDeviceModel, desiredToggleState?: boolean): void => {
        if (device !== undefined && this.states[device.deviceId] !== undefined) {
            if (desiredToggleState !== undefined) {
                this.states[device.deviceId].minimized = desiredToggleState;
            } else {
                this.states[device.deviceId].minimized = !this.states[device.deviceId].minimized;
            }
            if (!this.states[device.deviceId].poppedOut) {
                if (this.states[device.deviceId].minimized) {
                    this.globalEventBusService.broadcast(GlobalEvents.remoteControlMinimized, null);
                } else {
                    this.globalEventBusService.broadcast(GlobalEvents.remoteControlMaximized, null);
                }
            }
        }
    };

    public isRemoteViewOnly = (device: IDeviceModel): boolean => {
        const rcPermissions = this.getPermissions(device.deviceId);
        if (!rcPermissions) {
            return false;
        }
        const isRemoteViewOnly =
            (device && device.isRemoteViewOnly) ||
            ((rcPermissions.permissions & hd.RemoteControlPermissions.RemoteControl) ===
                hd.RemoteControlPermissions.RemoteControl &&
                (rcPermissions.permissions & hd.RemoteControlPermissions.RemoteControlInput) === 0);
        return isRemoteViewOnly;
    };

    public getStateByMcDeviceId = (mcDeviceId: string): RemoteControlDeviceState => {
        if (!this.states[mcDeviceId]) {
            for (const [_, value] of Object.entries(this.states)) {
                if (value.device && mcDeviceId === value.device.mcDeviceId) {
                    return value;
                }
            }
        } else {
            return this.states[mcDeviceId];
        }
    };

    private isVideoRecordingInprogress = (): boolean => {
        const state = this.getCurrentState();
        return (
            state &&
            (state.recordingState === RecordingState.UploadingVideo ||
                state.recordingState === RecordingState.Running ||
                state.recordingState === RecordingState.Paused ||
                state.recordingState === RecordingState.PendingAccept)
        );
    };

    private findAndRemoveCallback = (
        callbackMap: DeviceIdCallbackMap,
        removeFunction: Function,
        removeDeviceId: string
    ) => {
        const callbackArray = callbackMap[removeDeviceId];
        const index = callbackArray ? callbackArray.indexOf(removeFunction, 0) : -1;
        if (index > -1) {
            callbackArray[index] = () => { };
        }
    };

    private isFileUploadOrDownloadInProgress = (): boolean => this.fileManagerService.isCommandInProgress;

    public canPopout = (device: IDeviceModel): boolean => {
        const state = this.fileManagerService.getState(device?.deviceId || device?.mcDeviceId);
        return (
            ((!state || state.canPopout()) &&
                !this.isRecording(device) &&
                !this.isRecordingPendingAccept() &&
                !this.fileManagerService.isCommandInProgress)
        );
    };

    /** Checks if there is any operation that would require a Discard confirmation from the user */
    public isDiscardBlocked = (): boolean => this.isVideoRecordingInprogress() || this.fileManagerService.isCommandInProgress;

    public getDiscardBlockedReason = (): string => {
        if (this.isVideoRecordingInprogress()) {
            return this.saI18nPipe.transform('ticket_Cancel_VideoRecording_Message');
        } else if (this.fileManagerService.isCommandInProgress) {
            return this.saI18nPipe.transform('ticket_Cancel_File_Upload_Download_Message');
        } else {
            console.error('There is no reason to request user confirmation');
        }
    };

    public setRemoteControlStartedCallback = (callback: Function, deviceId: string): void => {
        if (this.remoteControlStartedCallback[deviceId] === undefined) {
            this.remoteControlStartedCallback[deviceId] = [];
        }
        this.remoteControlStartedCallback[deviceId].push(callback);
    };

    public setRemoteControlEndedCallback = (callback: Function, deviceId: string): void => {
        if (this.remoteControlEndedCallback[deviceId] === undefined) {
            this.remoteControlEndedCallback[deviceId] = [];
        }
        this.remoteControlEndedCallback[deviceId].push(callback);
    };

    public setRemoteControlErrorCallback = (callback: Function, deviceId: string): void => {
        if (this.remoteControlErrorCallback[deviceId] === undefined) {
            this.remoteControlErrorCallback[deviceId] = [];
        }
        this.remoteControlErrorCallback[deviceId].push(callback);
    };

    public setRemoteControlLoginCallback = (callback: Function, deviceId: string): void => {
        if (this.remoteControlLoginCallback[deviceId] === undefined) {
            this.remoteControlLoginCallback[deviceId] = [];
        }
        this.remoteControlLoginCallback[deviceId].push(callback);
    };

    public removeRemoteControlStartedCallback = (callback: Function, deviceId: string): void => {
        this.findAndRemoveCallback(this.remoteControlStartedCallback, callback, deviceId);
    };

    public removeRemoteControlEndedCallback = (callback: Function, deviceId: string): void => {
        this.findAndRemoveCallback(this.remoteControlEndedCallback, callback, deviceId);
    };

    public removeRemoteControlErrorCallback = (callback: Function, deviceId: string): void => {
        this.findAndRemoveCallback(this.remoteControlErrorCallback, callback, deviceId);
    };

    public removeRemoteControlLoginCallback = (callback: Function, deviceId: string): void => {
        this.findAndRemoveCallback(this.remoteControlLoginCallback, callback, deviceId);
    };

    public addRemoteControlPopoutCallback = (callback: (poppedOut: boolean) => void): void => {
        this.remoteControlPopoutCallbacks.push(callback);
    };

    public removeRemoteControlPopoutCallback = (callback: (poppedOut: boolean) => void): void => {
        const index = this.remoteControlPopoutCallbacks.indexOf(callback);
        if (index > -1) {
            this.remoteControlPopoutCallbacks.splice(index, 1);
        }
    };

    public setStopRecordingCallback = (callback: Function): void => {
        this.stopRecordingCallback = callback;
    };

    public getStopRecordingCallback = (): Function => this.stopRecordingCallback;

    public setStartRecordingCallback = (callback: Function): void => {
        this.startRecordingCallback = callback;
    };

    public getStartRecordingCallback = (): Function => this.startRecordingCallback;

    public onError = (
        mcDeviceId: string,
        errorMessage: string,
        errorMessageCaption: string = '',
        errorMessageDescription: string = ''
    ): void => {
        const state = this.getStateByMcDeviceId(mcDeviceId);
        if (state !== undefined) {
            state.errorMessage = errorMessage;
            state.hasError = true;
            state.running = false;
            state.connected = false;
            state.loading = false;
            state.active = false;
            state.recording = false;
            state.recording$;
            state.errorMessageCaption = errorMessageCaption;
            state.errorMessageDescription = errorMessageDescription;

            this.remoteControlErrorBroadcast.postMessage(null);
        }

        const callbackArray = this.remoteControlErrorCallback[state && state.device ? state.device.deviceId : mcDeviceId];
        if (callbackArray) {
            if (callbackArray.length > 0) {
                for (const callback of callbackArray) {
                    callback();
                }
            }
        }
    };

    public onSessionStarted = (deviceId: string): void => {
        // Set start time for RC session
        const state = this.states[deviceId];
        if (state) {
            state.startTime = new Date();
        }

        // Fire registered callbacks
        const callbackArray = this.remoteControlStartedCallback[deviceId];
        if (callbackArray !== undefined && callbackArray.length > 0) {
            for (const callback of callbackArray) {
                callback();
            }
        }
    }

    public onSessionEnded = (deviceId: string): void => {
        const callbackArray = this.remoteControlEndedCallback[deviceId];
        if (callbackArray !== undefined && callbackArray.length > 0) {
            for (const callback of callbackArray) {
                callback();
            }
        }
    }

    public onSessionLogin = (deviceId: string): void => {
        const callbackArray = this.remoteControlLoginCallback[deviceId];
        if (callbackArray !== undefined && callbackArray.length > 0) {
            for (const callback of callbackArray) {
                callback();
            }
        }
    };

    /** Processes session popout callbacks with a popouts state notification,
     * popout opening when popoutOpen is true, clossing when false
     */
    public onSessionPopout = (popoutOpen: boolean): void => {
        for (const callback of this.remoteControlPopoutCallbacks) {
            callback(popoutOpen);
        }
    };
}
